import { useEffect, useMemo, useState } from 'react';
import { Empty, Input, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useListPatientsQuery } from '@/state/slices/patients';
import type { PatientListItem } from '@/models/patient';
import styles from './AssignPatientModal.module.css';

interface LinkExistingTabProps {
  selectedUuid: string | null;
  onSelect: (patient: PatientListItem) => void;
}

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return iso.slice(0, 10);
};

const useDebounced = <T,>(value: T, delay = 300): T => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
};

export default function LinkExistingTab({
  selectedUuid,
  onSelect,
}: LinkExistingTabProps) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query.trim(), 300);

  // Skip the request entirely until the user types something — no point
  // hammering /patients/ with the unfiltered first page on modal open.
  const { data, isFetching } = useListPatientsQuery(
    { page: 1, size: 8, q: debounced },
    { skip: debounced.length < 1 }
  );

  const results = useMemo<PatientListItem[]>(() => data?.results ?? [], [data]);

  return (
    <>
      <div className={styles.searchRow}>
        <Input
          allowClear
          autoFocus
          prefix={<SearchOutlined />}
          placeholder="Search by name or MRN"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {debounced.length === 0 && (
        <div className={styles.empty}>
          Type a name or MRN to search the patient list.
        </div>
      )}
      {debounced.length > 0 && isFetching && (
        <div className={styles.empty}>
          <Spin />
        </div>
      )}
      {debounced.length > 0 && !isFetching && results.length === 0 && (
        <Empty
          description="No patients matched"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}
      {debounced.length > 0 && !isFetching && results.length > 0 && (
        <div className={styles.results} role="listbox">
          {results.map((p) => {
            const isSelected = p.uuid === selectedUuid;
            return (
              <div
                key={p.uuid}
                role="option"
                aria-selected={isSelected}
                className={`${styles.resultRow}${
                  isSelected ? ` ${styles.selected}` : ''
                }`}
                onClick={() => onSelect(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(p);
                  }
                }}
                tabIndex={0}
              >
                <span className={styles.resultName}>{p.fullName || '—'}</span>
                <span className={styles.resultMrn}>
                  {p.medicalRecordNumber || '—'}
                </span>
                <span className={styles.resultMeta}>
                  DOB {formatDate(p.dateOfBirth)}
                </span>
                <span className={styles.resultMeta}>
                  Last study {formatDate(p.latestStudyDate)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
